export const KBD_FIELD_ATTR = 'data-kbd-field'
export const KBD_ORDER_ATTR = 'data-kbd-order'
export const KBD_SUBMIT_ATTR = 'data-kbd-submit'

export type KbdFieldType = 'default' | 'tag' | 'select' | 'textarea'

export type KbdFieldPropsInput = 'default' | 'text' | 'email' | 'password' | 'textarea' | 'select' | 'combobox' | 'tag'

/** Spread onto focusable field elements (Input, SelectTrigger, Combobox, Switch, etc.). */
export function kbdFieldProps(type: KbdFieldPropsInput = 'default'): Record<string, string> {
	switch (type) {
		case 'textarea':
			return { [KBD_FIELD_ATTR]: 'textarea' }
		case 'select':
		case 'combobox':
			return { [KBD_FIELD_ATTR]: 'select' }
		case 'tag':
			return { [KBD_FIELD_ATTR]: 'tag' }
		default:
			return { [KBD_FIELD_ATTR]: '' }
	}
}

/** Spread onto the primary dialog CTA button. */
export function kbdSubmitProps(): Record<string, string> {
	return { [KBD_SUBMIT_ATTR]: '' }
}

const DOM_ORDER_MULTIPLIER = 1000

function getFieldType(element: HTMLElement): KbdFieldType {
	const raw = element.getAttribute(KBD_FIELD_ATTR)
	if (raw === 'tag') {
		return 'tag'
	}
	if (raw === 'select') {
		return 'select'
	}
	if (raw === 'textarea') {
		return 'textarea'
	}
	return 'default'
}

function getEffectiveOrder(element: HTMLElement, domIndex: number): number {
	const raw = element.getAttribute(KBD_ORDER_ATTR)
	if (raw !== null && raw !== '') {
		return Number(raw)
	}
	return domIndex * DOM_ORDER_MULTIPLIER
}

function isFieldVisible(element: HTMLElement): boolean {
	if (element.closest('[hidden]') !== null) {
		return false
	}
	if (element.getAttribute('aria-hidden') === 'true') {
		return false
	}

	const style = window.getComputedStyle(element)
	if (style.display === 'none' || style.visibility === 'hidden') {
		return false
	}

	return element.getClientRects().length > 0
}

function isFieldEnabled(element: HTMLElement): boolean {
	if (element.matches(':disabled')) {
		return false
	}
	if (element.getAttribute('aria-disabled') === 'true') {
		return false
	}
	return true
}

function isFieldReadOnly(element: HTMLElement): boolean {
	if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
		return element.readOnly
	}
	return element.getAttribute('aria-readonly') === 'true'
}

export function getKbdFields(container: HTMLElement): HTMLElement[] {
	const candidates = Array.from(container.querySelectorAll<HTMLElement>(`[${KBD_FIELD_ATTR}]`))
	const domIndex = new Map<HTMLElement, number>()
	candidates.forEach((element, index) => {
		domIndex.set(element, index)
	})

	return candidates
		.filter(element => isFieldVisible(element) && isFieldEnabled(element) && !isFieldReadOnly(element))
		.sort((a, b) => {
			const orderDiff = getEffectiveOrder(a, domIndex.get(a) ?? 0) - getEffectiveOrder(b, domIndex.get(b) ?? 0)
			if (orderDiff !== 0) {
				return orderDiff
			}
			return (domIndex.get(a) ?? 0) - (domIndex.get(b) ?? 0)
		})
}

function getFocusedField(eventTarget: EventTarget | null): HTMLElement | null {
	if (!(eventTarget instanceof HTMLElement)) {
		return null
	}
	return eventTarget.closest<HTMLElement>(`[${KBD_FIELD_ATTR}]`)
}

function getFieldInputValue(field: HTMLElement): string {
	if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
		return field.value.trim()
	}

	const nestedInput = field.querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea')
	return nestedInput?.value.trim() ?? ''
}

function isTextareaField(field: HTMLElement): boolean {
	if (getFieldType(field) === 'textarea') {
		return true
	}
	return field instanceof HTMLTextAreaElement
}

function isSelectLikeField(field: HTMLElement): boolean {
	if (getFieldType(field) === 'select') {
		return true
	}
	if (field.getAttribute('role') === 'combobox') {
		return true
	}
	if (field.matches('[data-slot="select-trigger"]')) {
		return true
	}
	if (field.getAttribute('aria-haspopup') === 'listbox') {
		return true
	}
	return false
}

export function focusKbdField(field: HTMLElement): void {
	field.focus()

	if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
		const length = field.value.length
		field.setSelectionRange(length, length)
		field.scrollIntoView({ block: 'nearest' })
		return
	}

	const nestedInput = field.querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea')
	if (nestedInput !== null) {
		nestedInput.focus()
		const length = nestedInput.value.length
		nestedInput.setSelectionRange(length, length)
	}

	field.scrollIntoView({ block: 'nearest' })
}

function clickKbdSubmit(container: HTMLElement): void {
	const submitButton = container.querySelector<HTMLButtonElement>(`[${KBD_SUBMIT_ATTR}]`)
	if (submitButton === null) {
		return
	}
	if (submitButton.disabled || submitButton.getAttribute('aria-disabled') === 'true') {
		return
	}
	submitButton.click()
}

function shouldIgnoreIme(event: KeyboardEvent): boolean {
	return event.isComposing || event.keyCode === 229
}

function isCmdOrCtrlEnter(event: KeyboardEvent): boolean {
	return event.key === 'Enter' && (event.metaKey || event.ctrlKey)
}

function isPlainEnter(event: KeyboardEvent): boolean {
	return event.key === 'Enter' && !event.metaKey && !event.ctrlKey && !event.altKey && !event.shiftKey
}

function advanceOrSubmit(container: HTMLElement, field: HTMLElement): void {
	const fields = getKbdFields(container)
	const currentIndex = fields.indexOf(field)
	if (currentIndex === -1) {
		return
	}

	if (currentIndex < fields.length - 1) {
		focusKbdField(fields[currentIndex + 1])
		return
	}

	clickKbdSubmit(container)
}

function getKbdNavContainer(field: HTMLElement): HTMLElement | null {
	const dialog = field.closest('[data-slot="dialog-content"]')
	if (dialog instanceof HTMLElement) {
		return dialog
	}

	let ancestor: HTMLElement | null = field.parentElement
	while (ancestor !== null) {
		if (
			ancestor.querySelector(`[${KBD_SUBMIT_ATTR}]`) !== null &&
			ancestor.querySelector(`[${KBD_FIELD_ATTR}]`) !== null
		) {
			return ancestor
		}
		ancestor = ancestor.parentElement
	}

	return null
}

/**
 * After a select/combobox picks a value, move focus to the next tagged field (dialog forms only).
 * Never submits — picking a dropdown value is not an intent to submit, so when the field is the
 * last one we leave focus put rather than firing the submit CTA (which would close the form, e.g.
 * selecting the aggregator in a panel edit). Only an explicit Enter keypress submits on the last field.
 */
export function advanceFromKbdField(field: HTMLElement): void {
	const kbdField = field.matches(`[${KBD_FIELD_ATTR}]`) ? field : field.closest<HTMLElement>(`[${KBD_FIELD_ATTR}]`)
	if (kbdField === null) {
		return
	}

	const container = getKbdNavContainer(kbdField)
	if (container === null) {
		return
	}

	const fields = getKbdFields(container)
	const currentIndex = fields.indexOf(kbdField)
	if (currentIndex === -1 || currentIndex >= fields.length - 1) {
		return
	}

	focusKbdField(fields[currentIndex + 1])
}

export function handleFormKeyboardNav(container: HTMLElement, event: KeyboardEvent): void {
	if (event.key !== 'Enter') {
		return
	}
	if (shouldIgnoreIme(event)) {
		return
	}

	if (isCmdOrCtrlEnter(event)) {
		event.preventDefault()
		clickKbdSubmit(container)
		return
	}

	if (!isPlainEnter(event)) {
		return
	}
	if (event.defaultPrevented) {
		return
	}

	const field = getFocusedField(event.target)
	if (field === null) {
		return
	}

	if (isSelectLikeField(field)) {
		return
	}

	if (isTextareaField(field)) {
		return
	}

	if (getFieldType(field) === 'tag' && getFieldInputValue(field) !== '') {
		return
	}

	event.preventDefault()
	advanceOrSubmit(container, field)
}
